import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, clearAllState } from '../redux/slices/authSlice';
import { maybeRefreshAuthToken } from '../services/api';

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes — long forms often have pauses while looking up info
const THROTTLE_MS = 15 * 1000; // For high-frequency events (mousemove, scroll), reset at most once per 15 seconds
// Keep JWT warm while the tab is open so long form work never hits an expired token on submit.
const SESSION_HEARTBEAT_MS = 5 * 60 * 1000;

// Capture phase on document: many UI handlers call stopPropagation() on click/mousedown, which blocks
// bubbling to window — those interactions must still count as activity. Scroll on inner overflow
// elements does not bubble; capture receives it on the way down to the scrolling target.
const CAPTURE = true;

const InactivityLogout = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated);
  const timeoutRef = useRef(null);
  const heartbeatRef = useRef(null);
  const lastResetRef = useRef(Date.now());

  const handleLogout = useCallback(() => {
    dispatch(logout());
    dispatch(clearAllState());
    window.location.href = '/login';
  }, [dispatch]);

  const resetTimer = useCallback((options = {}) => {
    const { force = false } = options;
    if (!isAuthenticated) return;

    const now = Date.now();
    // For throttled calls (e.g. from mousemove/scroll), only reset if enough time has passed or force
    if (!force && now - lastResetRef.current < THROTTLE_MS) {
      return;
    }
    lastResetRef.current = now;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_TIMEOUT_MS);
  }, [isAuthenticated, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      return;
    }

    resetTimer({ force: true });
    // Renew immediately on login / remount in case the stored JWT is already near expiry.
    void maybeRefreshAuthToken();

    // Events that always reset immediately (user is clearly active)
    const immediateEvents = [
      'pointerdown',
      'mousedown',
      'keydown',
      'click',
      'touchstart',
      'input',
      'change',
      'focusin',
    ];
    const throttledEvents = ['mousemove', 'scroll', 'wheel'];

    const handleImmediate = () => {
      resetTimer({ force: true });
      void maybeRefreshAuthToken();
    };
    const handleThrottled = () => {
      resetTimer();
      // Mouse/scroll-only work (reading long forms) must still keep the JWT alive.
      void maybeRefreshAuthToken();
    };

    // When user returns to this tab, reset idle timer and refresh if the token aged while hidden.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetTimer({ force: true });
        void maybeRefreshAuthToken();
      }
    };

    immediateEvents.forEach((event) =>
      document.addEventListener(event, handleImmediate, CAPTURE)
    );
    throttledEvents.forEach((event) =>
      document.addEventListener(event, handleThrottled, { capture: CAPTURE, passive: true })
    );
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const handleAppActivity = () => {
      resetTimer({ force: true });
      void maybeRefreshAuthToken();
    };
    window.addEventListener('appActivity', handleAppActivity);

    heartbeatRef.current = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void maybeRefreshAuthToken();
    }, SESSION_HEARTBEAT_MS);

    return () => {
      immediateEvents.forEach((event) =>
        document.removeEventListener(event, handleImmediate, CAPTURE)
      );
      throttledEvents.forEach((event) =>
        document.removeEventListener(event, handleThrottled, { capture: CAPTURE, passive: true })
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('appActivity', handleAppActivity);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };
  }, [isAuthenticated, resetTimer]);

  return null;
};

export default InactivityLogout;
